import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './RequisitionApprovals.css';
import { apiFetch, getApiUrl } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { formatAppDate } from '../../utils/dateUtils';

// Requisition Details Panel Component - moved outside to avoid forwardRef issues
const RequisitionDetailsPanel = ({ requisition, details, loading, actionLoading, onApprove, onReject, onClose }) => {
  const { t } = useTranslation('supervisor');
  const { showError } = useMessages();
  const [itemApprovals, setItemApprovals] = useState({});
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Initialize item approvals when details load
  useEffect(() => {
    if (details?.items) {
      const initialApprovals = {};
      details.items.forEach(item => {
        initialApprovals[item.requisition_item_id] = item.requested_quantity;
      });
      setItemApprovals(initialApprovals);
    }
  }, [details]);

  const handleQuantityChange = (itemId, quantity) => {
    setItemApprovals(prev => ({
      ...prev,
      [itemId]: parseFloat(quantity) || 0
    }));
  };

  const handleApproveClick = () => {
    if (!details?.items) {
      showError(t('requisitionApprovals.messages.approvalError'), t('requisitionApprovals.messages.noItemsToApprove'));
      return;
    }
    const itemApprovalsList = details.items.map(item => ({
      requisition_item_id: item.requisition_item_id,
      approved_quantity: itemApprovals[item.requisition_item_id] || 0
    }));
    onApprove(itemApprovalsList, approvalNotes);
  };

  const handleRejectClick = () => {
    if (!rejectionReason.trim()) {
      showError(t('requisitionApprovals.messages.rejectionError'), t('requisitionApprovals.messages.provideRejectionReason'));
      return;
    }
    onReject(rejectionReason);
    setShowRejectModal(false);
  };

  return (
    <div className="details-panel">
      <div className="panel-header">
        <h3>{t('requisitionApprovals.details.title')}</h3>
        <button className="btn-close" onClick={onClose}>✕</button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('requisitionApprovals.messages.loadingDetails')}</p>
        </div>
      ) : details && details.header ? (
        <div className="panel-content">
          <div className="requisition-info">
            <div className="info-grid">
              <div><strong>{t('requisitionApprovals.details.requisition')}:</strong> {details.header.requisition_number}</div>
              <div><strong>{t('requisitionApprovals.details.productionOrder')}:</strong> {details.header.po_number}</div>
              <div><strong>{t('requisitionApprovals.details.customer')}:</strong> {details.header.customer_name || 'N/A'}</div>
              <div><strong>{t('requisitionApprovals.details.task')}:</strong> {details.header.task_name || t('requisitionApprovals.details.general')}</div>
              <div><strong>{t('requisitionApprovals.details.requestedBy')}:</strong> {details.header.requested_by_name}</div>
              <div><strong>{t('requisitionApprovals.details.priority')}:</strong> {details.header.priority}</div>
              <div><strong>{t('requisitionApprovals.details.requested')}:</strong> {formatAppDate(details.header.requested_date, 'datetime')}</div>
            </div>
            {details.header.notes && (
              <div className="notes-section">
                <strong>{t('requisitionApprovals.details.notes')}:</strong>
                <p>{details.header.notes}</p>
              </div>
            )}
            {details.header.is_over_bom && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginTop: '10px' }}>
                <strong>⚠ {t('requisitionApprovals.overBom.request')}</strong>
                <p style={{ margin: '4px 0 0' }}>{details.header.over_bom_reason || t('requisitionApprovals.overBom.noReason')}</p>
              </div>
            )}
          </div>

          <div className="items-section">
            <h4>{t('requisitionApprovals.details.requestedItems')}</h4>
            <div className="items-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('requisitionApprovals.headers.item')}</th>
                    <th>{t('requisitionApprovals.headers.requestedQty')}</th>
                    <th>{t('requisitionApprovals.headers.availableStock')}</th>
                    <th>{t('requisitionApprovals.headers.approveQty')}</th>
                    <th>{t('requisitionApprovals.headers.uom')}</th>
                    <th>{t('requisitionApprovals.headers.notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {details.items && details.items.map((item) => (
                    <tr key={item.requisition_item_id}>
                      <td>
                        <div className="item-info">
                          <strong>{item.item_name}</strong>
                          <small>{item.item_code}</small>
                        </div>
                      </td>
                      <td className="quantity-cell">{item.requested_quantity}</td>
                      <td className="stock-cell">
                        <span className={item.available_quantity < item.requested_quantity ? 'low-stock' : 'adequate-stock'}>
                          {item.available_quantity || 0}
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          max={item.available_quantity || item.requested_quantity}
                          value={itemApprovals[item.requisition_item_id] || 0}
                          onChange={(e) => handleQuantityChange(item.requisition_item_id, e.target.value)}
                          className="quantity-input"
                        />
                      </td>
                      <td>{item.unit_of_measure}</td>
                      <td className="notes-cell">{item.item_notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="approval-section">
            <div className="notes-input">
              <label>{t('requisitionApprovals.details.approvalNotes')}:</label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={t('requisitionApprovals.details.approvalNotesPlaceholder')}
                rows="3"
              />
            </div>

            <div className="action-buttons">
              <button
                className="btn-approve"
                onClick={handleApproveClick}
                disabled={actionLoading}
              >
                {actionLoading ? t('requisitionApprovals.buttons.processing') : `✅ ${t('requisitionApprovals.buttons.approveRequisition')}`}
              </button>
              <button
                className="btn-reject"
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
              >
                ❌ {t('requisitionApprovals.buttons.rejectRequisition')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('requisitionApprovals.messages.unableToLoadDetails')}</p>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="rejection-modal">
            <h3>{t('requisitionApprovals.rejection.title')}</h3>
            <p>{t('requisitionApprovals.rejection.prompt')}</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t('requisitionApprovals.rejection.placeholder')}
              rows="4"
              autoFocus
            />
            <div className="modal-buttons">
              <button onClick={() => setShowRejectModal(false)}>{t('requisitionApprovals.buttons.cancel')}</button>
              <button
                className="btn-reject"
                onClick={handleRejectClick}
                disabled={!rejectionReason.trim()}
              >
                {t('requisitionApprovals.buttons.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RequisitionApprovals = forwardRef(({ initialRequisitionId }, ref) => {
  const { t } = useTranslation('supervisor');
  const { showSuccess, showError } = useMessages();
  const [pendingRequisitions, setPendingRequisitions] = useState([]);
  const [approvedRequisitions, setApprovedRequisitions] = useState([]);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [requisitionDetails, setRequisitionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [actionLoading, setActionLoading] = useState(false);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchPendingRequisitions();
      fetchApprovedRequisitions();
      fetchApprovalHistory();
    }
  }));

  // Fetch pending requisitions (status = PENDING)
  const fetchPendingRequisitions = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(getApiUrl('/api/inventory/requisitions/pending'));
      const data = await response.json();
      setPendingRequisitions(data);
    } catch (error) {
      console.error('Error fetching pending requisitions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch approved requisitions that need picklists (status = APPROVED)
  const fetchApprovedRequisitions = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(getApiUrl('/api/inventory/requisitions/approved'));
      const data = await response.json();
      // Ensure data is an array
      if (Array.isArray(data)) {
        setApprovedRequisitions(data);
      } else if (data && Array.isArray(data.requisitions)) {
        setApprovedRequisitions(data.requisitions);
      } else {
        console.error('Unexpected data format:', data);
        setApprovedRequisitions([]);
      }
    } catch (error) {
      console.error('Error fetching approved requisitions:', error);
      setApprovedRequisitions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch approval history
  const fetchApprovalHistory = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/inventory/requisitions/history?days=30'));
      const data = await response.json();
      setApprovalHistory(data);
    } catch (error) {
      console.error('Error fetching approval history:', error);
    }
  };

  // Fetch requisition details
  const fetchRequisitionDetails = async (requisitionId) => {
    try {
      setDetailsLoading(true);
      const response = await apiFetch(getApiUrl(`/api/inventory/requisitions/${requisitionId}/details`));
      const data = await response.json();
      setRequisitionDetails(data);
    } catch (error) {
      console.error('Error fetching requisition details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle requisition selection
  const handleRequisitionSelect = (requisition) => {
    setSelectedRequisition(requisition);
    fetchRequisitionDetails(requisition.requisition_id);
  };

  // Handle approval with quantity adjustments
  const handleApprove = async (itemApprovals, notes) => {
    try {
      setActionLoading(true);
      const response = await apiFetch(getApiUrl('/api/inventory/requisitions/approve'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requisition_id: selectedRequisition.requisition_id,
          item_approvals: itemApprovals,
          notes: notes
        })
      });

      const result = await response.json();

      if (result.success) {
        showSuccess(
          t('requisitionApprovals.messages.requisitionApproved'),
          t('requisitionApprovals.messages.requisitionApprovedDetails', { requisitionNumber: selectedRequisition.requisition_number })
        );
        setSelectedRequisition(null);
        setRequisitionDetails(null);
        fetchPendingRequisitions();
        fetchApprovedRequisitions();
        fetchApprovalHistory();
      } else {
        showError(t('requisitionApprovals.messages.approvalFailed'), result.message);
      }
    } catch (error) {
      console.error('Error approving requisition:', error);
      showError(t('requisitionApprovals.messages.networkError'), t('requisitionApprovals.messages.failedToApprove'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle rejection
  const handleReject = async (rejectionReason) => {
    try {
      setActionLoading(true);
      const response = await apiFetch(getApiUrl('/api/inventory/requisitions/reject'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requisition_id: selectedRequisition.requisition_id,
          rejection_reason: rejectionReason
        })
      });

      const result = await response.json();

      if (result.success) {
        showSuccess(
          t('requisitionApprovals.messages.requisitionRejected'),
          t('requisitionApprovals.messages.requisitionRejectedDetails', { requisitionNumber: selectedRequisition.requisition_number })
        );
        setSelectedRequisition(null);
        setRequisitionDetails(null);
        fetchPendingRequisitions();
        fetchApprovedRequisitions();
        fetchApprovalHistory();
      } else {
        showError(t('requisitionApprovals.messages.rejectionFailed'), result.message);
      }
    } catch (error) {
      console.error('Error rejecting requisition:', error);
      showError(t('requisitionApprovals.messages.networkError'), t('requisitionApprovals.messages.failedToReject'));
    } finally {
      setActionLoading(false);
    }
  };

  // Note: Picklist generation moved to warehouse - supervisors only view approved requisitions

  useEffect(() => {
    fetchPendingRequisitions();
    fetchApprovedRequisitions();
    fetchApprovalHistory();
  }, []);

  // Opened from a dashboard card: select that requisition once it is in the pending list
  const openedRequisitionId = useRef(null);
  useEffect(() => {
    if (!initialRequisitionId || openedRequisitionId.current === initialRequisitionId) return;
    const requisition = pendingRequisitions.find(r => String(r.requisition_id) === String(initialRequisitionId));
    if (requisition) {
      openedRequisitionId.current = initialRequisitionId;
      setActiveTab('pending');
      handleRequisitionSelect(requisition);
    }
  }, [initialRequisitionId, pendingRequisitions]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatAppDate(dateString, 'datetime');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return '#dc3545';
      case 'HIGH': return '#fd7e14';
      case 'NORMAL': return '#007bff';
      case 'LOW': return '#6c757d';
      default: return '#007bff';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return '#28a745';
      case 'REJECTED': return '#dc3545';
      case 'PENDING': return '#ffc107';
      default: return '#6c757d';
    }
  };

  return (
    <div className="requisition-approvals">
      <div className="approvals-header">
        <h2>✅ {t('requisitionApprovals.title')}</h2>
        <div className="approvals-tabs">
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            📋 {t('requisitionApprovals.tabs.pendingApproval')} ({pendingRequisitions.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            ✅ {t('requisitionApprovals.tabs.approved', { default: 'Approved Requisitions' })} ({approvedRequisitions.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📚 {t('requisitionApprovals.tabs.history')}
          </button>
        </div>
      </div>

      <div className="approvals-content">
        {activeTab === 'pending' && (
          <div className="pending-tab">
            <div className="requisitions-list">
              <div className="list-header">
                <h3>{t('requisitionApprovals.sections.pendingRequisitions')}</h3>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('requisitionApprovals.messages.loadingPendingRequisitions')}</p>
                </div>
              ) : pendingRequisitions.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <p>{t('requisitionApprovals.messages.noPendingRequisitions')}</p>
                  <small>{t('requisitionApprovals.messages.allRequisitionsProcessed')}</small>
                </div>
              ) : (
                <div className="requisitions-grid">
                  {pendingRequisitions.map((requisition) => (
                    <div
                      key={requisition.requisition_id}
                      className={`requisition-card ${selectedRequisition?.requisition_id === requisition.requisition_id ? 'selected' : ''}`}
                      onClick={() => handleRequisitionSelect(requisition)}
                    >
                      <div className="card-header">
                        <span className="requisition-number">{requisition.requisition_number}</span>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {requisition.is_over_bom && (
                            <span style={{
                              fontSize: '11px', fontWeight: '600', padding: '2px 6px',
                              background: '#fff3cd', color: '#856404',
                              border: '1px solid #ffc107', borderRadius: '4px',
                              whiteSpace: 'nowrap'
                            }}>
                              ⚠ {t('requisitionApprovals.overBom.badge')}
                            </span>
                          )}
                          <span
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(requisition.priority) }}
                          >
                            {requisition.priority}
                          </span>
                        </div>
                      </div>
                      <div className="card-content">
                        <p><strong>{t('requisitionApprovals.labels.po')}:</strong> {requisition.po_number}</p>
                        <p><strong>{t('requisitionApprovals.labels.customer')}:</strong> {requisition.customer_name || 'N/A'}</p>
                        <p><strong>{t('requisitionApprovals.labels.task')}:</strong> {requisition.task_name || t('requisitionApprovals.details.general')}</p>
                        <p><strong>{t('requisitionApprovals.labels.requestedBy')}:</strong> {requisition.requested_by_name}</p>
                        <p><strong>{t('requisitionApprovals.labels.items')}:</strong> {requisition.item_count}</p>
                        <p><strong>{t('requisitionApprovals.labels.requested')}:</strong> {formatDate(requisition.requested_date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedRequisition && (
              <RequisitionDetailsPanel
                requisition={selectedRequisition}
                details={requisitionDetails}
                loading={detailsLoading}
                actionLoading={actionLoading}
                onApprove={handleApprove}
                onReject={handleReject}
                onClose={() => {
                  setSelectedRequisition(null);
                  setRequisitionDetails(null);
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'approved' && (
          <div className="approved-tab">
            <div className="approved-list">
              <div className="list-header">
                <h3>{t('requisitionApprovals.sections.approvedRequisitions', { default: 'Approved Requisitions' })}</h3>
                <small>{t('requisitionApprovals.sections.approvedNote', { default: 'These requisitions have been approved and are awaiting warehouse processing' })}</small>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('requisitionApprovals.messages.loadingApprovedRequisitions')}</p>
                </div>
              ) : approvedRequisitions.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p>{t('requisitionApprovals.messages.noApprovedRequisitions', { default: 'No approved requisitions awaiting processing' })}</p>
                  <small>{t('requisitionApprovals.messages.allProcessed', { default: 'All requisitions have been processed by warehouse' })}</small>
                </div>
              ) : (
                <div className="approved-grid">
                  {approvedRequisitions.map((requisition) => (
                    <div
                      key={requisition.requisition_id}
                      className="approved-card"
                    >
                      <div className="card-header">
                        <span className="requisition-number">{requisition.requisition_number}</span>
                        <span className="status-badge status-approved">{t('requisitionApprovals.status.approved')}</span>
                      </div>
                      <div className="card-content">
                        <p><strong>{t('requisitionApprovals.labels.po')}:</strong> {requisition.po_number}</p>
                        <p><strong>{t('requisitionApprovals.labels.customer')}:</strong> {requisition.customer_name || 'N/A'}</p>
                        <p><strong>{t('requisitionApprovals.labels.task')}:</strong> {requisition.task_name || t('requisitionApprovals.details.general')}</p>
                        <p><strong>{t('requisitionApprovals.labels.requestedBy')}:</strong> {requisition.requested_by_name}</p>
                        <p><strong>{t('requisitionApprovals.labels.items')}:</strong> {requisition.item_count}</p>
                        <p><strong>{t('requisitionApprovals.labels.approved')}:</strong> {formatDate(requisition.approved_date)}</p>
                        <div className="card-note">
                          <small>💡 {t('requisitionApprovals.messages.picklistGenerationNote', { default: 'Picklist generation is handled by warehouse staff' })}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-tab">
            <div className="history-header">
              <h3>{t('requisitionApprovals.sections.recentApprovalHistory')}</h3>
            </div>

            {approvalHistory.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📜</div>
                <p>{t('requisitionApprovals.messages.noApprovalHistory')}</p>
                <small>{t('requisitionApprovals.messages.noRecentlyProcessed')}</small>
              </div>
            ) : (
              <div className="history-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('requisitionApprovals.headers.requisition')}</th>
                      <th>{t('requisitionApprovals.headers.po')}</th>
                      <th>{t('requisitionApprovals.headers.requestedBy')}</th>
                      <th>{t('requisitionApprovals.headers.status')}</th>
                      <th>{t('requisitionApprovals.headers.approvedBy')}</th>
                      <th>{t('requisitionApprovals.headers.items')}</th>
                      <th>{t('requisitionApprovals.headers.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalHistory.map((item) => (
                      <tr key={item.requisition_id}>
                        <td>{item.requisition_number}</td>
                        <td>{item.po_number}</td>
                        <td>{item.requested_by_name}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(item.status) }}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td>{item.approved_by_name || '-'}</td>
                        <td>{item.item_count}</td>
                        <td>{formatDate(item.approved_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

RequisitionApprovals.displayName = 'RequisitionApprovals';

export default RequisitionApprovals;